import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

import type { TableMatrixCapabilities } from '../../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import {
  buildDrillDownTreeFromRows,
  drillTotalsAtBottom,
  drillTotalsAtRight,
  drillTotalsBlockPosition,
  drillTreeToResponse,
  normalizeDrillTotalsPlacement,
  resolveDrillColumnOrder,
  type DrillClientConfig,
  type DrillTotalsPlacement,
} from '../../../chart-components/unified-table-matrix-chart/drilldown-client-tree.utils';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile, DrillColumnOrderItem, DrillComputedColumn, DrillDownDimension, DrillDownMeasure, DrillYearHelperMeasure } from '../../dvm-layout-builder/dvm-layout-builder.types';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceFormulaService } from '../../shared/self-service-formula.service';
import { buildDrillDownWidgetData, parseDropdownOptions } from '../../shared/self-service-recompute.utils';
import type { FormulaTemplate } from '../../shared/self-service-types';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { buildDrillDownVisualRules, level2ColorConfig, yearFilterConfig } from './drill-format.utils';

/** Drill-down state restored from a legacy Pivot table (see TableMatrixStepComponent.legacyPivotToDrilldown). */
export interface MigratedPivotDrill {
  dimensions: DrillDownDimension[];
  columnDimensions: DrillDownDimension[];
  measures: DrillDownMeasure[];
}

/**
 * Drill-Down build form: hierarchy dimensions, measures (aggregation /
 * roll-up / editability / year scope), pivot column dimensions, formula
 * columns and column order, plus the client-side tree computation. Serves
 * the legacy DRILL_DOWN_TABLE layout on its own and is COMPOSED by the
 * table-matrix step for the Table & Matrix "Drill-Down" type.
 */
@Component({
  selector: 'app-drill-down-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, NgSelectModule],
  templateUrl: './drill-down-step.component.html',
  styles: [':host { display: contents; }'],
})
export class DrillDownStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['DRILL_DOWN_TABLE', 'TABLE_MATRIX'] as const;
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly ui = inject(SelfServiceUiService);

  /** The legacy stand-alone Drill-Down Table layout. */
  get isLegacyDrillDown(): boolean {
    return this.host.selectedLayoutType === 'DRILL_DOWN_TABLE';
  }
  /** Table & Matrix with the Drill-Down sub-type. */
  get isTableMatrixDrilldown(): boolean {
    return this.host.selectedLayoutType === 'TABLE_MATRIX' && this.host.isTmDrilldown;
  }
  override get active(): boolean {
    return this.isLegacyDrillDown || this.isTableMatrixDrilldown;
  }

  drillDownDimensions: DrillDownDimension[] = [];
  drillDownMeasures: DrillDownMeasure[] = [];
  drillDataMode: 'client' | 'server' = 'client';
  drillTotalsPlacement: DrillTotalsPlacement = 'both';
  drillColumnDimensions: DrillDownDimension[] = [];
  drillComputedColumns: DrillComputedColumn[] = [];
  drillColumnOrder: string[] = [];
  drillColumnOrderAccordionOpen = false;
  private drillColumnOrderMemo?: { sig: string; items: DrillColumnOrderItem[] };
  newDrillColumnName = '';
  newDrillColumnExpression = '';
  newDrillColumnAfter = '';
  newDrillColumnError = '';
  drillFormulaAccordionOpen = false;
  /** Server rows of the drill table being edited (client mode) — re-shaped on every form change. */
  readonly drillRowsCache = new Map<string, Array<Record<string, unknown>>>();

  private drillDimensionIdCounter = 0;
  private drillMeasureIdCounter = 0;
  private pendingPrune = '';

  get formulaBuilderEnabled(): boolean {
    return this.host.formulaBuilderConfig.enabled !== false;
  }
  /** Row-level formula templates (config `formulaBuilder.templates` of kind 'column'). */
  get formulaTemplates(): FormulaTemplate[] {
    const list = Array.isArray(this.host.formulaBuilderConfig.templates) ? this.host.formulaBuilderConfig.templates : [];
    return list.filter((t) => t.kind === 'column');
  }

  get rollUpStrategies(): LabelValueOption[] {
    return this.host.layout.options?.rollUpStrategies?.length
      ? this.host.layout.options.rollUpStrategies
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }

  onDrillDimensionFieldDrop(event: DragEvent, dimension?: DrillDownDimension): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    const target = dimension || { id: this.nextDimensionId(), field: '', label: '' };
    target.field = mapping.field;
    if (!target.label || /^Level \d+$/.test(target.label)) target.label = mapping.field;
    if (!dimension) this.drillDownDimensions = [...this.drillDownDimensions, target];
    this.onDrillDimensionChange();
  }

  onDrillMeasureFieldDrop(event: DragEvent, measure?: DrillDownMeasure): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    const target = measure || {
      id: this.nextMeasureId(), field: '', label: '', aggregation: 'NONE', rollUpStrategy: 'SUM', total: false,
    };
    target.field = mapping.field;
    if (!target.label || /^Measure \d+$/.test(target.label)) target.label = mapping.field;
    if (!measure) this.drillDownMeasures = [...this.drillDownMeasures, target];
    this.onDrillMeasureChange();
  }

  addDrillDimension(): void {
    const dimension: DrillDownDimension = {
      id: this.nextDimensionId(),
      field: '',
      label: `Level ${this.drillDownDimensions.length + 1}`,
      editable: false,
    };
    this.drillDownDimensions = [...this.drillDownDimensions, dimension];
  }

  parseValueOrder(raw: string | undefined): string[] {
    return String(raw ?? '')
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  valueOrderToText(raw: unknown): string {
    if (Array.isArray(raw)) return raw.map((value) => String(value).trim()).filter(Boolean).join(', ');
    return typeof raw === 'string' ? raw : '';
  }

  valueOrderPayload(dimension: DrillDownDimension): Record<string, string[]> {
    const order = this.parseValueOrder(dimension.valueOrder);
    return order.length ? { valueOrder: order } : {};
  }

  dropdownEditorPayload(row: { editable?: boolean; editor?: string; dropdownOptions?: string; dropdownFromData?: boolean }): Record<string, unknown> {
    if (!row.editable || row.editor !== 'dropdown') return {};
    return {
      editor: 'dropdown',
      dropdownOptions: parseDropdownOptions(row.dropdownOptions),
      ...(row.dropdownFromData ? { dropdownFromData: true } : {}),
    };
  }

  restoreDropdownEditor(item: Record<string, unknown>): { editor?: 'dropdown'; dropdownOptions?: string; dropdownFromData?: boolean } {
    if (item['editor'] !== 'dropdown') return {};
    return {
      editor: 'dropdown',
      dropdownOptions: parseDropdownOptions(item['dropdownOptions']).join(', '),
      ...(item['dropdownFromData'] === true ? { dropdownFromData: true } : {}),
    };
  }

  editableLevelsPayload(measure: { editable?: boolean; editableLevels?: number[] }): Record<string, number[]> {
    if (!measure.editable || !Array.isArray(measure.editableLevels)) return {};
    const max = this.drillDownDimensions.length;
    const levels = Array.from(new Set(measure.editableLevels.map(Number)))
      .filter((level) => Number.isInteger(level) && level >= 1 && level <= max)
      .sort((a, b) => a - b);
    return levels.length ? { editableLevels: levels } : {};
  }

  rowTotalHeaderPayload(measure: { rowTotalHeader?: string }): { rowTotalHeader?: string } {
    const header = String(measure.rowTotalHeader ?? '').trim();
    return header ? { rowTotalHeader: header } : {};
  }

  restoreEditableLevels(item: Record<string, unknown>): { editableLevels?: number[] } {
    const raw = item['editableLevels'];
    if (!Array.isArray(raw)) return {};
    const levels = Array.from(new Set(raw.map(Number))).filter((level) => Number.isInteger(level) && level >= 1).sort((a, b) => a - b);
    return levels.length ? { editableLevels: levels } : {};
  }

  private editableLevelOptionsCache: { key: string; items: Array<{ label: string; value: number }> } | null = null;
  get drillEditableLevelOptions(): Array<{ label: string; value: number }> {
    const dimensions = this.drillDownDimensions;
    const key = dimensions.map((dimension) => dimension.label || dimension.field || '').join('');
    if (this.editableLevelOptionsCache?.key === key) return this.editableLevelOptionsCache.items;
    const items = dimensions.map((dimension, index) => {
      const name = dimension.label || dimension.field || '';
      const suffix = index === dimensions.length - 1 ? ' (leaf rows)' : '';
      return { label: `Level ${index + 1}${name ? ` · ${name}` : ''}${suffix}`, value: index + 1 };
    });
    this.editableLevelOptionsCache = { key, items };
    return items;
  }
  onDrillMeasureLevelsChange(measure: DrillDownMeasure, levels: unknown): void {
    const picked = Array.isArray(levels) ? levels.map(Number).filter((level) => Number.isInteger(level) && level >= 1) : [];
    measure.editableLevels = picked.length ? picked : undefined;
    this.onDrillMeasureChange();
  }

  removeDrillDimension(dimension: DrillDownDimension): void {
    this.drillDownDimensions = this.drillDownDimensions.filter((item) => item.id !== dimension.id);
    this.host.invalidatePreview();
  }

  moveDrillDimensionUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillDimension(index, index - 1);
  }

  moveDrillDimensionDown(index: number): void {
    if (index >= this.drillDownDimensions.length - 1) return;
    this.reorderDrillDimension(index, index + 1);
  }

  private reorderDrillDimension(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownDimensions];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownDimensions = next;
    this.host.invalidatePreview();
  }

  onDrillDimensionChange(): void {
    this.drillDownDimensions = [...this.drillDownDimensions];
    this.host.invalidatePreview();
  }


  addDrillMeasure(): void {
    const measure: DrillDownMeasure = {
      id: this.nextMeasureId(),
      field: '',
      label: `Measure ${this.drillDownMeasures.length + 1}`,
      aggregation: 'NONE',
      rollUpStrategy: 'SUM',
      total: false,
      editable: false,
    };
    this.drillDownMeasures = [...this.drillDownMeasures, measure];
  }

  removeDrillMeasure(measure: DrillDownMeasure): void {
    this.drillDownMeasures = this.drillDownMeasures.filter((item) => item.id !== measure.id);
    this.host.invalidatePreview();
  }

  moveDrillMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillMeasure(index, index - 1);
  }

  moveDrillMeasureDown(index: number): void {
    if (index >= this.drillDownMeasures.length - 1) return;
    this.reorderDrillMeasure(index, index + 1);
  }

  private reorderDrillMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownMeasures = next;
    this.host.invalidatePreview();
  }

  onDrillMeasureChange(): void {
    this.drillDownMeasures = [...this.drillDownMeasures];
    this.host.invalidatePreview();
  }


  private readonly drillDataModesFallback: LabelValueOption[] = [
    { label: 'Client-side (multiple datasets, pivot columns, formulas)', value: 'client' },
    { label: 'Backend tree (legacy dimensions + measures compute)', value: 'server' },
  ];
  get drillDataModes(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['drillDataModes'] as LabelValueOption[] | undefined)
      ?? this.drillDataModesFallback;
  }
  readonly drillMeasureKinds: LabelValueOption[] = [
    { label: 'Number (aggregated)', value: 'number' },
    { label: 'Text (attribute / comment)', value: 'text' },
  ];
  readonly drillMeasureYearScopes: LabelValueOption[] = [
    { label: 'All years (default)', value: 'all' },
    { label: 'Latest year only', value: 'latest' },
  ];
  onDrillMeasureYearScopeChange(measure: DrillDownMeasure, value: string | null): void {
    if (value === 'latest') {
      measure.yearScope = 'latest';
      if (!String(measure.yearField || '').trim()) measure.yearField = this.defaultYearFieldFor(measure) || '';
    } else {
      measure.yearScope = undefined;
    }
    this.onDrillMeasureChange();
  }
  private defaultYearFieldFor(measure: DrillDownMeasure): string | undefined {
    const columns = this.columnsForDrillRow(measure);
    return columns.find((c) => /^(fiscal_?year|fy|year)$/i.test(c)) || columns.find((c) => /year/i.test(c));
  }
  yearScopePayload(m: { yearScope?: string; yearField?: string; showYearColumn?: boolean }): { yearScope?: 'latest'; yearField?: string; showYearColumn?: true } {
    if (m.yearScope !== 'latest') return {};
    const field = String(m.yearField || '').trim();
    return { yearScope: 'latest', ...(field ? { yearField: field } : {}), ...(m.showYearColumn === true ? { showYearColumn: true as const } : {}) };
  }
  drillYearHelperMeasures(): DrillYearHelperMeasure[] {
    const real = new Set(this.drillDownMeasures.map((m) => `${m.dataset || ''}|${m.field}`));
    const seen = new Set<string>();
    const helpers: DrillYearHelperMeasure[] = [];
    this.drillDownMeasures.forEach((m) => {
      const field = m.yearScope === 'latest' ? String(m.yearField || '').trim() : '';
      if (!field) return;
      const key = `${m.dataset || ''}|${field}`;
      if (real.has(key) || seen.has(key)) return;
      seen.add(key);
      const show = this.drillDownMeasures.some((x) =>
        x.yearScope === 'latest' && x.showYearColumn === true && String(x.yearField || '').trim() === field && (x.dataset || '') === (m.dataset || ''));
      helpers.push({
        field,
        label: show ? this.humanizeFieldName(field) : field,
        aggregation: 'MAX', rollUpStrategy: 'MAX', total: false, hidden: !show, yearHelper: true as const,
        ...(show ? { pivot: false as const, kind: 'text' as const, yearScope: 'latest' as const, yearField: field } : {}),
        ...(m.dataset ? { dataset: m.dataset } : {}),
      });
    });
    return helpers;
  }
  private humanizeFieldName(field: string): string {
    return String(field || '').replace(/[_\s]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  readonly drillEditorKinds: LabelValueOption[] = [
    { label: 'Input (type a value)', value: 'input' },
    { label: 'Dropdown (pick from a list)', value: 'dropdown' },
  ];
  get isDrillClientMode(): boolean {
    return this.isTableMatrixDrilldown && this.drillDataMode === 'client';
  }
  columnsForDrillRow(row: { id: string; dataset?: string }): string[] {
    return this.host.columnsForRow(row.id, row.dataset || this.host.drillDataset);
  }
  isDrillRowLoading(row: { dataset?: string }): boolean {
    return this.host.isDatasetColumnsLoading(row.dataset || this.host.drillDataset);
  }
  drillRowHasDataset(row: { dataset?: string }): boolean {
    return !!(row.dataset || this.host.drillDataset);
  }
  onDrillRowDatasetChange(row: DrillDownDimension | DrillDownMeasure, dataset: string | null, kind: 'dimension' | 'columnDimension' | 'measure'): void {
    const next = dataset || '';
    row.dataset = next && next !== this.host.drillDataset ? next : '';
    row.field = '';
    if (next) this.host.loadColumnsForDataset(next);
    if (kind === 'dimension') this.onDrillDimensionChange();
    else if (kind === 'columnDimension') this.onDrillColumnDimensionChange();
    else this.onDrillMeasureChange();
  }
  onDrillDataModeChange(mode: string): void {
    this.drillDataMode = mode === 'server' ? 'server' : 'client';
    this.host.invalidatePreview();
  }
  private readonly drillTotalsPlacementsFallback: LabelValueOption[] = [
    { label: 'Bottom and right (total row + Total block)', value: 'both' },
    { label: 'Bottom and left (total row + Total block after the first column)', value: 'both-left' },
    { label: 'Right only (Total block across pivot columns)', value: 'right' },
    { label: 'Left only (Total block after the first column)', value: 'left' },
    { label: 'Bottom only (grand-total row)', value: 'bottom' },
    { label: 'None', value: 'none' },
  ];
  get drillTotalsPlacements(): LabelValueOption[] {
    return ((this.host.layout.options as Record<string, unknown> | undefined)?.['drillTotalsPlacements'] as LabelValueOption[] | undefined)
      ?? this.drillTotalsPlacementsFallback;
  }
  onDrillTotalsPlacementChange(value: string): void {
    this.drillTotalsPlacement = normalizeDrillTotalsPlacement(value);
    this.host.invalidatePreview();
  }

  addDrillColumnDimension(): void {
    this.drillColumnDimensions = [...this.drillColumnDimensions, { id: this.nextDimensionId(), field: '', label: '' }];
  }
  removeDrillColumnDimension(dimension: DrillDownDimension): void {
    this.drillColumnDimensions = this.drillColumnDimensions.filter((item) => item.id !== dimension.id);
    this.host.invalidatePreview();
  }
  onDrillColumnDimensionChange(): void {
    this.drillColumnDimensions = [...this.drillColumnDimensions];
    this.host.invalidatePreview();
  }
  onDrillColumnDimensionFieldDrop(event: DragEvent, dimension?: DrillDownDimension): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    if (!this.host.selectedDatasets.length) this.host.useDroppedSingleDataset(mapping.dataset);
    else this.host.loadColumnsForDataset(mapping.dataset);
    const target = dimension || { id: this.nextDimensionId(), field: '', label: '' };
    target.dataset = mapping.dataset !== this.host.drillDataset ? mapping.dataset : '';
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!dimension) this.drillColumnDimensions = [...this.drillColumnDimensions, target];
    this.onDrillColumnDimensionChange();
  }

  get drillFormulaColumns(): string[] {
    const names = new Set<string>();
    this.drillDownMeasures.forEach((m) => { if (m.label) names.add(m.label); if (m.field) names.add(m.field); });
    this.drillComputedColumns.forEach((c) => names.add(c.name));
    return [...names];
  }
  private drillFormulaAnchorMemo: { sig: string; items: LabelValueOption[] } | null = null;
  get drillFormulaAnchorOptions(): LabelValueOption[] {
    const measures = this.drillDownMeasures.filter((m) => m.field);
    const sig = [...measures.map((m) => `${m.label}|${m.field}`), ...this.drillComputedColumns.map((c) => c.name)].join('\u0001');
    if (this.drillFormulaAnchorMemo?.sig === sig) return this.drillFormulaAnchorMemo.items;
    const items: LabelValueOption[] = [
      { label: 'End (after the last measure)', value: '' },
      ...measures.map((m) => ({ label: `After: ${m.label || m.field}`, value: m.label || m.field })),
      ...this.drillComputedColumns.map((c) => ({ label: `After formula: ${c.name}`, value: c.name })),
    ];
    this.drillFormulaAnchorMemo = { sig, items };
    return items;
  }
  drillFormulaAnchorLabel(after?: string): string {
    if (!after) return '';
    const measure = this.drillDownMeasures.find((m) => (m.label || m.field) === after || m.field === after);
    return measure ? (measure.label || measure.field) : after;
  }
  get drillColumnOrderPreset(): string[] {
    const raw = (this.host.layout.options as Record<string, unknown> | undefined)?.['drillColumnOrderPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  get drillPivotValueOrderPreset(): string[] {
    const raw = (this.host.layout.options as Record<string, unknown> | undefined)?.['drillPivotValueOrderPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  get drillRowTotalColumnsPreset(): string[] {
    const raw = (this.host.layout.options as Record<string, unknown> | undefined)?.['drillRowTotalColumnsPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  get drillColumnOrderPresetLabel(): string {
    return this.drillColumnOrderPreset.map((entry) => entry.split('|')[0].trim()).filter(Boolean).join(' · ');
  }
  get drillColumnOrderItems(): DrillColumnOrderItem[] {
    const measures = this.drillDownMeasures.filter((m) => m.field);
    const hasPivot = this.drillColumnDimensions.some((d) => !!d.field);
    const sig = [
      ...measures.map((m) => `${m.label}|${m.field}|${m.pivot === false ? 0 : 1}|${m.hidden ? 1 : 0}`), '#',
      ...this.drillComputedColumns.map((c) => `${c.name}|${c.after || ''}`), '#',
      ...this.drillColumnOrder, '#', ...this.drillColumnOrderPreset, String(hasPivot),
    ].join('\u0001');
    if (this.drillColumnOrderMemo?.sig === sig) return this.drillColumnOrderMemo.items;
    const items: DrillColumnOrderItem[] = resolveDrillColumnOrder(measures, this.drillComputedColumns, this.drillColumnOrder, this.drillColumnOrderPreset)
      .filter((item) => item.kind === 'formula' || !measures[item.index].hidden)
      .map((item) => ({
        key: item.key,
        orderKey: item.kind === 'measure' ? measures[item.index].field : item.key,
        kind: item.kind,
        split: hasPivot && (item.kind === 'formula' || measures[item.index].pivot !== false),
      }));
    this.drillColumnOrderMemo = { sig, items };
    return items;
  }
  moveDrillColumn(index: number, delta: number): void {
    const keys = this.drillColumnOrderItems.map((item) => item.orderKey);
    const to = index + delta;
    if (index < 0 || index >= keys.length || to < 0 || to >= keys.length) return;
    const [moved] = keys.splice(index, 1);
    keys.splice(to, 0, moved);
    this.drillColumnOrder = keys;
    this.host.invalidatePreview();
  }
  resetDrillColumnOrder(): void {
    if (!this.drillColumnOrder.length) return;
    this.drillColumnOrder = [];
    this.host.invalidatePreview();
  }
  applyDrillFormulaTemplate(tpl: FormulaTemplate): void {
    this.newDrillColumnName = tpl.defaultName;
    this.newDrillColumnExpression = tpl.expressionTemplate;
    this.newDrillColumnError = '';
  }
  private normalizeDrillFormula(raw: string): string {
    return String(raw ?? '')
      .replace(/[‒–—−]/g, '-')
      .replace(/[×]/g, '*')
      .replace(/[÷]/g, '/')
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .trim();
  }
  private static readonly DRILL_FORMULA_FUNCS = new Set([
    'IF', 'IFERROR', 'SUM', 'AVERAGE', 'COUNT', 'COUNTIF', 'CONCAT', 'CONCATENATE', 'AND', 'OR', 'NOT', 'TRUE', 'FALSE',
    'ABS', 'ROUND', 'CEIL', 'FLOOR', 'SQRT', 'LOG', 'EXP', 'POW', 'MIN', 'MAX', 'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'SUBSTR',
    'REPLACE', 'CONTAINS', 'STARTSWITH', 'ENDSWITH', 'YEAR', 'MONTH', 'DAY', 'DAYOFWEEK', 'WEIGHTEDAVERAGE', 'VARP', 'VARS',
    'STDEVP', 'STDEVS', 'E', 'PI',
  ]);
  private resolveDrillFormulaRefs(expression: string): { expression: string; unknown: string[] } {
    const known = this.drillFormulaColumns;
    const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const byNorm = new Map<string, string>();
    known.forEach((n) => { if (!byNorm.has(norm(n))) byNorm.set(norm(n), n); });
    const unknown = new Set<string>();
    let out = expression.replace(/\[([^\]]+)\]/g, (_match, raw: string) => {
      const name = raw.trim();
      if (known.includes(name)) return `[${name}]`;
      const hit = byNorm.get(norm(name));
      if (hit) return `[${hit}]`;
      unknown.add(name);
      return `[${name}]`;
    });
    const stripped = out.replace(/\[[^\]]+\]/g, ' ').replace(/"[^"]*"|'[^']*'/g, ' ');
    const tokens = Array.from(new Set(stripped.match(/[A-Za-z_][A-Za-z0-9_]*/g) || []));
    tokens.forEach((token) => {
      if (known.includes(token) || DrillDownStepComponent.DRILL_FORMULA_FUNCS.has(token.toUpperCase())) return;
      const hit = byNorm.get(norm(token));
      const pattern = new RegExp(`(?<![\\w\\]\\[])${token}(?![\\w\\[(])`, 'g');
      if (hit) out = out.replace(pattern, `[${hit}]`);
      else unknown.add(token);
    });
    return { expression: out, unknown: [...unknown] };
  }
  insertDrillFormulaRef(name: string): void {
    const current = this.newDrillColumnExpression || '';
    this.newDrillColumnExpression = `${current}${current && !/s$/.test(current) ? ' ' : ''}[${name}]`;
    this.newDrillColumnError = '';
  }
  addDrillComputedColumn(): void {
    const name = this.newDrillColumnName.trim();
    const typed = this.normalizeDrillFormula(this.newDrillColumnExpression);
    this.newDrillColumnError = '';
    const resolved = typed ? this.resolveDrillFormulaRefs(typed) : { expression: typed, unknown: [] as string[] };
    const expression = resolved.expression;
    if (resolved.unknown.length) {
      const names = this.drillFormulaColumns.map((n) => `[${n}]`).join(', ');
      this.newDrillColumnError = `Unknown name${resolved.unknown.length > 1 ? 's' : ''} ${resolved.unknown.map((u) => `[${u}]`).join(', ')} — this table's measures are: ${names || '(add a measure first)'}. Use those names (or rename the measure labels first).`;
      return;
    }
    const cfg = this.host.formulaBuilderConfig;
    if (!name) { this.newDrillColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newDrillColumnError = 'Expression is required.'; return; }
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[A-Za-z_][A-Za-z0-9_ ]*$/;
    if (!pattern.test(name)) { this.newDrillColumnError = cfg.nameError || 'Invalid column name.'; return; }
    if (this.drillFormulaColumns.includes(name)) { this.newDrillColumnError = `A column named "${name}" already exists.`; return; }
    try {
      this.formulaService.buildRowEvaluator(expression, this.drillFormulaColumns);
    } catch (e) {
      const names = this.drillFormulaColumns.map((n) => `[${n}]`).join(', ');
      this.newDrillColumnError = `Invalid expression: ${e instanceof Error ? e.message : 'parse error'}. Wrap names in brackets — available here: ${names || '(add a measure first)'}.`;
      return;
    }
    const after = this.newDrillColumnAfter.trim();
    this.drillComputedColumns = [...this.drillComputedColumns, { name, expression, ...(after ? { after } : {}) }];
    this.newDrillColumnName = '';
    this.newDrillColumnExpression = '';
    this.newDrillColumnAfter = '';
    this.host.invalidatePreview();
  }
  removeDrillComputedColumn(name: string): void {
    this.drillComputedColumns = this.drillComputedColumns.filter((c) => c.name !== name);
    this.host.invalidatePreview();
  }

  private drillClientConfig(): DrillClientConfig {
    const dim = (d: DrillDownDimension) => {
      const order = this.parseValueOrder(d.valueOrder);
      return { field: d.field, label: d.label || undefined, dataset: d.dataset || undefined, ...(order.length ? { valueOrder: order } : {}) };
    };
    return {
      dimensions: this.drillDownDimensions.map(dim),
      columnDimensions: this.drillColumnDimensions.map(dim),
      measures: [
        ...this.drillDownMeasures.map((m) => ({
          field: m.field, label: m.label || undefined, aggregation: m.aggregation, total: !!m.total, dataset: m.dataset || undefined,
          ...this.rowTotalHeaderPayload(m),
          ...(m.pivot === false ? { pivot: false } : {}),
          ...(m.kind === 'text' ? { kind: 'text' as const } : {}),
          ...(m.hidden ? { hidden: true } : {}),
          ...this.yearScopePayload(m),
        })),
        ...this.drillYearHelperMeasures().filter((helper) => !helper.hidden),
      ],
      computedColumns: this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) })),
      ...(this.drillColumnOrder.length ? { columnOrder: [...this.drillColumnOrder] } : {}),
      ...(this.drillColumnOrderPreset.length ? { columnOrderPreset: [...this.drillColumnOrderPreset] } : {}),
      ...(this.drillPivotValueOrderPreset.length ? { columnValueOrderPreset: [...this.drillPivotValueOrderPreset] } : {}),
      ...(this.drillRowTotalColumnsPreset.length ? { rowTotalColumns: [...this.drillRowTotalColumnsPreset] } : {}),
      rawLeafRows: this.host.barFormat['drillRawLeafRows'] === true,
      ...(this.drillHeaderGroupByFrom(this.host.barFormat) === 'column' ? { headerGroupBy: 'column' as const } : {}),
      grandTotalRow: drillTotalsAtBottom(this.drillTotalsPlacement),
      ...this.drillRowTotalConfig(this.drillTotalsPlacement, this.host.barFormat),
    };
  }
  private tmFormatOrDefault(format: Record<string, unknown> | undefined, key: string): unknown {
    const raw = format?.[key];
    if (raw !== undefined && raw !== null && raw !== '') return raw;
    return this.host.applicableFormatControls('TABLE_MATRIX').find((control) => control.key === key)?.default;
  }
  drillRowTotalConfig(
    placement: DrillTotalsPlacement,
    format: Record<string, unknown> | undefined,
  ): { rowTotal?: boolean; rowTotalLabel?: string; rowTotalPosition?: 'left' | 'right' } {
    if (!drillTotalsAtRight(placement)) return {};
    const label = String(this.tmFormatOrDefault(format, 'drillRowTotalLabel') ?? '').trim();
    return {
      rowTotal: true,
      ...(label ? { rowTotalLabel: label } : {}),
      ...(drillTotalsBlockPosition(placement) === 'left' ? { rowTotalPosition: 'left' as const } : {}),
    };
  }
  drillHeaderGroupByFrom(format: Record<string, unknown> | undefined): 'measure' | 'column' | undefined {
    const raw = this.tmFormatOrDefault(format, 'drillHeaderGroupBy');
    return raw === 'column' || raw === 'measure' ? raw : undefined;
  }
  drillClientConfigFromCaps(caps: Record<string, unknown>): DrillClientConfig {
    const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
    const placement = normalizeDrillTotalsPlacement(caps['totalsPlacement']);
    return {
      dimensions: list<DrillClientConfig['dimensions'][number]>(caps['dimensions']),
      columnDimensions: list<NonNullable<DrillClientConfig['columnDimensions']>[number]>(caps['columnDimensions']),
      measures: list<DrillClientConfig['measures'][number]>(caps['measures']),
      grandTotalRow: drillTotalsAtBottom(placement),
      computedColumns: list<{ name: string; expression: string }>(caps['computedColumns']),
      columnOrder: list<string>(caps['columnOrder']),
      columnOrderPreset: list<string>(caps['columnOrderPreset']),
      columnValueOrderPreset: list<string>(caps['columnValueOrderPreset']),
      rowTotalColumns: list<string>(caps['rowTotalColumns']),
      rawLeafRows: caps['rawLeafRows'] === true,
      ...(caps['headerGroupBy'] === 'column' ? { headerGroupBy: 'column' as const } : {}),
      ...(caps['rowTotal'] === true
        ? {
            rowTotal: true,
            ...(caps['rowTotalLabel'] ? { rowTotalLabel: String(caps['rowTotalLabel']) } : {}),
            ...(caps['rowTotalPosition'] === 'left' ? { rowTotalPosition: 'left' as const } : {}),
          }
        : {}),
    };
  }
  buildDrillClientTreeResponse(rows: Array<Record<string, unknown>>, config: DrillClientConfig = this.drillClientConfig()): Record<string, unknown> {
    const tree = buildDrillDownTreeFromRows(rows, config, (expression, names) => this.formulaService.buildRowEvaluator(expression, names));
    return drillTreeToResponse(tree);
  }
  buildDrillClientComputePayload(): Record<string, unknown> {
    const seen = new Set<string>();
    const columns: Array<{ dataset: string; field: string; displayName: string }> = [];
    const push = (dataset: string | undefined, field: string): void => {
      const ds = dataset || this.host.drillDataset;
      const key = `${ds}|${field}`;
      if (!field || seen.has(key)) return;
      seen.add(key);
      columns.push({ dataset: ds, field, displayName: field });
    };
    this.drillDownDimensions.forEach((d) => push(d.dataset, d.field));
    this.drillColumnDimensions.forEach((d) => push(d.dataset, d.field));
    this.drillDownMeasures.forEach((m) => push(m.dataset, m.field));
    const expressions = this.drillComputedColumns.map((c) => c.expression).join(' ');
    if (expressions) {
      (this.host.cachedColumns(this.host.drillDataset) || []).forEach((field) => {
        if (field && expressions.includes(field)) push(this.host.drillDataset, field);
      });
    }
    this.drillDownMeasures.forEach((m) => {
      if (m.yearScope === 'latest') push(m.dataset, String(m.yearField || '').trim());
    });
    return {
      componentId: this.host.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: this.host.tmType,
      dashboardId: this.host.dashboardId,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      columns,
      aggregation: 'none',
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  nextDimensionId(): string {
    this.drillDimensionIdCounter += 1;
    return `drill-dimension-${this.drillDimensionIdCounter}`;
  }

  nextMeasureId(): string {
    this.drillMeasureIdCounter += 1;
    return `drill-measure-${this.drillMeasureIdCounter}`;
  }

  buildDrillDownWidgetData(response: Record<string, unknown> | null, format?: Record<string, unknown>): Record<string, unknown> {
    const shared = buildDrillDownWidgetData(response, {
      widgetId: 'dvm-layout-builder-drilldown-preview',
      fallbackHeaderName: this.drillDownDimensions[0]?.label,
      format,
      dimensions: this.drillDownDimensions,
      measures: this.drillDownMeasures,
      computedColumns: this.drillComputedColumns,
      formulaEvaluator: (expression, names) => this.formulaService.buildRowEvaluator(expression, names),
    });
    const measureKeys = ((shared['headers'] as Array<{ prop: string; expandabile?: boolean }>) || [])
      .filter((header) => !header.expandabile)
      .map((header) => header.prop);
    const fmt = format || {};
    return {
      ...shared,
      title: this.host.title.trim(),
      levelFields: shared['levelFields'] ?? {},
      enableExpandCollapseAll: fmt['enableExpandCollapseAll'] === true,
      enableExport: fmt['enableExport'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      enableSavedViews: fmt['enableSavedViews'] === true,
      showColumnDividers: fmt['showColumnDividers'] === true,
      recalcTotalsOnEdit: fmt['drillEditRecalcTotals'] !== false,
      ...yearFilterConfig(fmt),
      visualRules: buildDrillDownVisualRules(measureKeys, fmt),
      ...level2ColorConfig(fmt),
    };
  }

  /** The drill-down part of Table & Matrix capabilities (dimensions / measures / pivot / formulas / totals). */
  applyToTableMatrixCapabilities(caps: TableMatrixCapabilities, fmt: Record<string, unknown>): void {
    caps.dimensions = this.drillDownDimensions.map((d) => ({
      field: d.field,
      label: d.label,
      editable: !!d.editable,
      ...this.dropdownEditorPayload(d),
      ...(d.dataset ? { dataset: d.dataset } : {}),
      ...this.valueOrderPayload(d),
    }));
    caps.measures = [
      ...this.drillDownMeasures.map((m) => ({
        field: m.field,
        label: m.label,
        aggregation: m.aggregation,
        rollUpStrategy: m.rollUpStrategy,
        total: !!m.total,
        editable: !!m.editable,
        ...this.dropdownEditorPayload(m),
        ...this.editableLevelsPayload(m),
        ...(m.dataset ? { dataset: m.dataset } : {}),
        ...(m.pivot === false ? { pivot: false } : {}),
        ...(m.kind === 'text' ? { kind: 'text' as const } : {}),
        ...(m.hidden ? { hidden: true } : {}),
        ...this.rowTotalHeaderPayload(m),
        ...this.yearScopePayload(m),
      })),
      ...this.drillYearHelperMeasures(),
    ];
    if (this.drillDataMode === 'client') {
      caps.drillDataMode = 'client';
      if (this.drillColumnDimensions.length) {
        caps.columnDimensions = this.drillColumnDimensions.map((d) => ({
          field: d.field,
          ...(d.label ? { label: d.label } : {}),
          ...(d.dataset ? { dataset: d.dataset } : {}),
          ...this.valueOrderPayload(d),
        }));
      }
    }
    if (this.drillComputedColumns.length) {
      caps.computedColumns = this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) }));
    }
    if (this.drillColumnOrder.length) caps.columnOrder = [...this.drillColumnOrder];
    if (this.drillColumnOrderPreset.length) caps.columnOrderPreset = [...this.drillColumnOrderPreset];
    if (this.drillPivotValueOrderPreset.length) caps.columnValueOrderPreset = [...this.drillPivotValueOrderPreset];
    if (this.drillRowTotalColumnsPreset.length) caps.rowTotalColumns = [...this.drillRowTotalColumnsPreset];
    if (fmt['drillRawLeafRows'] === true) caps.rawLeafRows = true;
    const headerGroupBy = this.drillHeaderGroupByFrom(fmt);
    if (headerGroupBy) caps.headerGroupBy = headerGroupBy;
    caps.totalsPlacement = this.drillTotalsPlacement;
    Object.assign(caps, this.drillRowTotalConfig(this.drillTotalsPlacement, fmt));
  }

  /** The drill-down fields of the Table & Matrix generate payload. */
  tableMatrixPayloadFields(): Record<string, unknown> {
    return {
      dimensions: this.drillDownDimensions.map((dimension, index) => ({
        level: index + 1,
        field: dimension.field,
        label: dimension.label,
        ...this.valueOrderPayload(dimension),
        ...(dimension.editable ? { editable: true } : {}),
        ...this.dropdownEditorPayload(dimension),
        ...(dimension.dataset ? { dataset: dimension.dataset } : {}),
      })),
      measures: [
        ...this.drillDownMeasures.map((measure) => ({
          field: measure.field,
          label: measure.label,
          aggregation: measure.aggregation,
          rollUpStrategy: measure.rollUpStrategy,
          total: !!measure.total,
          ...(measure.editable ? { editable: true } : {}),
          ...this.dropdownEditorPayload(measure),
          ...this.editableLevelsPayload(measure),
          ...(measure.dataset ? { dataset: measure.dataset } : {}),
          ...(measure.pivot === false ? { pivot: false } : {}),
          ...(measure.kind === 'text' ? { kind: 'text' } : {}),
          ...(measure.hidden ? { hidden: true } : {}),
          ...this.rowTotalHeaderPayload(measure),
          ...this.yearScopePayload(measure),
        })),
        ...this.drillYearHelperMeasures(),
      ],
      totalsPlacement: this.drillTotalsPlacement,
      ...(this.drillDataMode === 'client'
        ? {
            dataMode: 'client',
            ...(this.drillColumnDimensions.length
              ? {
                  columnDimensions: this.drillColumnDimensions.map((dimension) => ({
                    field: dimension.field,
                    ...(dimension.label ? { label: dimension.label } : {}),
                    ...(dimension.dataset ? { dataset: dimension.dataset } : {}),
                    ...this.valueOrderPayload(dimension),
                  })),
                }
              : {}),
          }
        : {}),
      ...(this.drillComputedColumns.length
        ? { computedColumns: this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) })) }
        : {}),
      ...(this.drillColumnOrder.length ? { columnOrder: [...this.drillColumnOrder] } : {}),
      ...(this.drillColumnOrderPreset.length ? { columnOrderPreset: [...this.drillColumnOrderPreset] } : {}),
      ...(this.drillPivotValueOrderPreset.length ? { columnValueOrderPreset: [...this.drillPivotValueOrderPreset] } : {}),
      ...(this.drillRowTotalColumnsPreset.length ? { rowTotalColumns: [...this.drillRowTotalColumnsPreset] } : {}),
    };
  }

  /** Restore the drill state from a Table & Matrix tile (drill-down sub-type). */
  loadFromTableMatrix(tile: DashboardTile, payload: Record<string, unknown>, caps: Record<string, unknown>, migratedPivot: MigratedPivotDrill | null): void {
    if (migratedPivot) {
      this.drillDownDimensions = migratedPivot.dimensions;
      this.drillColumnDimensions = migratedPivot.columnDimensions;
      this.drillDownMeasures = migratedPivot.measures;
      this.drillComputedColumns = [];
      this.drillColumnOrder = [];
      this.drillDataMode = 'client';
      this.drillTotalsPlacement = 'both';
      return;
    }
    const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
    const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
    const toDimension = (item: Record<string, unknown>): DrillDownDimension => ({
      id: this.nextDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
      valueOrder: this.valueOrderToText(item['valueOrder']),
      editable: item['editable'] === true,
      ...this.restoreDropdownEditor(item),
      dataset: String(item['dataset'] ?? ''),
    });
    this.drillDownDimensions = dimensions.map(toDimension);
    this.drillDownMeasures = measures.filter((item) => item['yearHelper'] !== true).map((item) => ({
      id: this.nextMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
      aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
      editable: item['editable'] === true,
      ...this.restoreDropdownEditor(item),
      ...this.restoreEditableLevels(item),
      dataset: String(item['dataset'] ?? ''),
      ...(item['pivot'] === false ? { pivot: false as const } : {}),
      ...(item['kind'] === 'text' ? { kind: 'text' as const } : {}),
      ...(item['hidden'] === true ? { hidden: true } : {}),
      ...this.yearScopePayload({ yearScope: typeof item['yearScope'] === 'string' ? item['yearScope'] : undefined, yearField: typeof item['yearField'] === 'string' ? item['yearField'] : undefined, showYearColumn: item['showYearColumn'] === true }),
      ...this.rowTotalHeaderPayload({ rowTotalHeader: typeof item['rowTotalHeader'] === 'string' ? item['rowTotalHeader'] : undefined }),
    }));
    const savedColumnDims = (Array.isArray(payload['columnDimensions']) ? payload['columnDimensions'] : (Array.isArray(caps['columnDimensions']) ? caps['columnDimensions'] : [])) as Array<Record<string, unknown>>;
    this.drillColumnDimensions = savedColumnDims.map(toDimension);
    const savedComputed = (Array.isArray(payload['computedColumns']) ? payload['computedColumns'] : (Array.isArray(caps['computedColumns']) ? caps['computedColumns'] : [])) as Array<Record<string, unknown>>;
    this.drillComputedColumns = savedComputed
      .map((c) => ({ name: String(c['name'] ?? ''), expression: String(c['expression'] ?? ''), ...(c['after'] ? { after: String(c['after']) } : {}) }))
      .filter((c) => c.name && c.expression);
    const savedColumnOrder = (Array.isArray(payload['columnOrder']) ? payload['columnOrder'] : (Array.isArray(caps['columnOrder']) ? caps['columnOrder'] : [])) as unknown[];
    this.drillColumnOrder = savedColumnOrder.map((k) => String(k ?? '').trim()).filter(Boolean);
    this.drillDataMode = (payload['dataMode'] ?? caps['drillDataMode']) === 'client' ? 'client' : 'server';
    this.drillTotalsPlacement = normalizeDrillTotalsPlacement(payload['totalsPlacement'] ?? caps['totalsPlacement']);
    new Set([...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures].map((row) => row.dataset).filter((ds): ds is string => !!ds))
      .forEach((ds) => this.host.loadColumnsForDataset(ds));
  }

  /** Generate is refused while a level / pivot column / measure has no column, or names one its dataset lacks. */
  staleFieldsError(): string {
    const rows = [...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures];
    const stale = rows.filter((row) => {
      const columns = this.host.cachedColumns(row.dataset || this.host.drillDataset);
      return !!columns && !!row.field && !columns.includes(row.field);
    });
    if (stale.length || rows.some((row) => !row.field)) {
      return stale.length
        ? `${stale.map((row) => `Dataset "${row.dataset || this.host.drillDataset}" has no column '${row.field}'`).join('; ')} — re-pick those fields for their dataset.`
        : 'Pick a column for every hierarchy level, pivot column and measure.';
    }
    return '';
  }

  /** Drop the fields the new primary dataset does not have (deferred until its columns are known). */
  pruneFieldsForDataset(dataset: string): void {
    if (!dataset || (!this.drillDownDimensions.length && !this.drillDownMeasures.length)) return;
    const columns = this.host.cachedColumns(dataset);
    if (!columns) {
      this.pendingPrune = dataset;
      return;
    }
    this.pendingPrune = '';
    const known = new Set(columns);
    let changed = false;
    this.drillDownDimensions.forEach((dimension) => {
      if (dimension.field && !known.has(dimension.field)) {
        dimension.field = '';
        changed = true;
      }
    });
    this.drillDownMeasures.forEach((measure) => {
      if (measure.field && !known.has(measure.field)) {
        measure.field = '';
        changed = true;
      }
    });
    if (changed) {
      this.drillDownDimensions = [...this.drillDownDimensions];
      this.drillDownMeasures = [...this.drillDownMeasures];
      this.host.invalidatePreview();
      this.ui.showError(`Some fields pointed at columns "${dataset}" doesn't have — re-pick them for the new dataset.`);
    }
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.drillDownDimensions.length) missing.push('At least one hierarchy dimension');
    if (!this.drillDownMeasures.length) missing.push('At least one measure');
    return missing;
  }

  preGenerateError(): string {
    return this.staleFieldsError();
  }

  /** Legacy DRILL_DOWN_TABLE payload (the Table & Matrix variant is composed by the table-matrix step). */
  buildPayload(): Record<string, unknown> {
    return {
      componentId: this.host.componentId,
      componentType: 'TABLE',
      tableType: this.host.selectedLayoutType,
      dashboardId: this.host.dashboardId,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      dimensions: this.drillDownDimensions.map((dimension, index) => ({
        level: index + 1,
        field: dimension.field,
        label: dimension.label,
        ...this.valueOrderPayload(dimension),
        ...(dimension.editable ? { editable: true } : {}),
        ...this.dropdownEditorPayload(dimension),
      })),
      measures: this.drillDownMeasures.map((measure) => ({
        field: measure.field,
        label: measure.label,
        aggregation: measure.aggregation,
        rollUpStrategy: measure.rollUpStrategy,
        total: !!measure.total,
        ...(measure.editable ? { editable: true } : {}),
        ...this.dropdownEditorPayload(measure),
        ...this.editableLevelsPayload(measure),
      })),
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
    const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
    this.host.barFormat = { ...(tile.format || {}) };
    this.drillDownDimensions = dimensions.map((item) => ({
      id: this.nextDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
      valueOrder: this.valueOrderToText(item['valueOrder']),
      editable: item['editable'] === true,
      ...this.restoreDropdownEditor(item),
    }));
    this.drillDownMeasures = measures.map((item) => ({
      id: this.nextMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
      aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
      editable: item['editable'] === true,
      ...this.restoreDropdownEditor(item),
      ...this.restoreEditableLevels(item),
    }));
  }

  reset(): void {
    this.drillDownDimensions = [];
    this.drillDownMeasures = [];
    this.drillDataMode = 'client';
    this.drillTotalsPlacement = 'both';
    this.drillColumnDimensions = [];
    this.drillComputedColumns = [];
    this.drillColumnOrder = [];
    this.newDrillColumnName = '';
    this.newDrillColumnExpression = '';
    this.newDrillColumnAfter = '';
    this.newDrillColumnError = '';
  }

  onDatasetChanged(dataset: string): void {
    this.pruneFieldsForDataset(dataset);
  }

  onDatasetColumnsLoaded(dataset: string): void {
    if (this.pendingPrune === dataset) this.pruneFieldsForDataset(dataset);
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    const drillData = this.buildDrillDownWidgetData(response, base.format);
    drillData['widgetId'] = 'dvm-tile-drill-' + base.id;
    return { ...base, drillData };
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.drillData) return;
    tile.drillData = { ...tile.drillData, decimals: Number(format['decimals'] ?? 0) };
  }

  applyRecomputeResult(tile: DashboardTile, { result, format }: TileRecomputeResult): boolean {
    const drillData = this.buildDrillDownWidgetData(result, format);
    drillData['widgetId'] = 'dvm-tile-drill-' + tile.id;
    tile.drillData = drillData;
    return true;
  }
}
